import { Outlet, Navigate, useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { BottomNavigation } from './BottomNavigation';
import { WelcomeOnboarding, useWelcomeOnboarding } from './WelcomeOnboarding';
import { useEffect, useState, useCallback } from 'react';

const API = process.env.REACT_APP_BACKEND_URL;

// ── Sidebar SVG Icons (same style as BottomNavigation) ──

const AccueilIcon = ({ isActive }) => (
  <svg width="20" height="20" viewBox="0 0 28 28" fill="none"
    strokeLinecap="round" strokeLinejoin="round">
    <path d="M4 12L14 4L24 12V24H18V17H10V24H4V12Z"
      stroke={isActive ? '#3c6df0' : '#737373'}
      strokeWidth={isActive ? 2.5 : 2} fill="none" />
    <circle cx="14" cy="15" r="1.5"
      fill={isActive ? '#3c6df0' : '#737373'} />
  </svg>
);

const ColisIcon = ({ isActive }) => (
  <svg width="20" height="20" viewBox="0 0 28 28" fill="none"
    strokeLinecap="round" strokeLinejoin="round">
    <path d="M5 8L14 4L23 8V20L14 24L5 20V8Z"
      stroke={isActive ? '#3c6df0' : '#737373'}
      strokeWidth={isActive ? 2.5 : 2} fill="none" />
    <path d="M5 8L14 12L23 8"
      stroke={isActive ? '#3c6df0' : '#737373'}
      strokeWidth={isActive ? 2.5 : 2} fill="none" />
    <path d="M14 12V24"
      stroke={isActive ? '#3c6df0' : '#737373'}
      strokeWidth={isActive ? 2.5 : 2} fill="none" />
  </svg>
);

const TrajetsIcon = ({ isActive }) => (
  <svg width="20" height="20" viewBox="0 0 28 28" fill="none"
    strokeLinecap="round" strokeLinejoin="round">
    <path
      d="M6 19L11 14M11 14L8 7L12 9L14 6L18 13M11 14L18 13M18 13L22 15L20 19L13 17"
      stroke={isActive ? '#3c6df0' : '#737373'}
      strokeWidth={isActive ? 2.5 : 2} fill="none" />
  </svg>
);

const MessagesIcon = ({ isActive }) => (
  <svg width="20" height="20" viewBox="0 0 28 28" fill="none"
    strokeLinecap="round" strokeLinejoin="round">
    <path
      d="M5 6H23C23.6 6 24 6.4 24 7V18C24 18.6 23.6 19 23 19H9L4 24V7C4 6.4 4.4 6 5 6Z"
      stroke={isActive ? '#3c6df0' : '#737373'}
      strokeWidth={isActive ? 2.5 : 2} fill="none" />
    <path d="M9 11H19"
      stroke={isActive ? '#3c6df0' : '#737373'}
      strokeWidth={isActive ? 2 : 1.5} fill="none" />
    <path d="M9 14.5H15"
      stroke={isActive ? '#3c6df0' : '#737373'}
      strokeWidth={isActive ? 2 : 1.5} fill="none" />
  </svg>
);

const ProfilIcon = ({ isActive }) => (
  <svg width="20" height="20" viewBox="0 0 28 28" fill="none"
    strokeLinecap="round" strokeLinejoin="round">
    <circle cx="14" cy="9" r="4"
      stroke={isActive ? '#3c6df0' : '#737373'}
      strokeWidth={isActive ? 2.5 : 2} fill="none" />
    <path d="M6 23C6 19.1 9.6 16 14 16C18.4 16 22 19.1 22 23"
      stroke={isActive ? '#3c6df0' : '#737373'}
      strokeWidth={isActive ? 2.5 : 2} fill="none" />
    <path d="M18 18L20 19.5V22C20 23.1 19.2 24 18.3 24.4L18 24.5L17.7 24.4C16.8 24 16 23.1 16 22V19.5L18 18Z"
      stroke={isActive ? '#3c6df0' : '#737373'}
      strokeWidth={1.5} fill="none" />
  </svg>
);

// ── Nav items ──────────────────────────────────────────
const NAV_ITEMS = [
  { id: 'accueil',  label: 'Accueil',   Icon: AccueilIcon,  route: '/app' },
  { id: 'colis',    label: 'Colis',     Icon: ColisIcon,    route: '/colis' },
  { id: 'trajets',  label: 'Trajets',   Icon: TrajetsIcon,  route: '/trajets' },
  { id: 'messages', label: 'Messages',  Icon: MessagesIcon, route: '/messages',
    hasBadge: true },
  { id: 'profil',   label: 'Profil',    Icon: ProfilIcon,   route: '/profile' },
];

// ── Active tab detection ───────────────────────────────
function getActiveTab(pathname) {
  if (pathname === '/app' || pathname === '/dashboard') return 'accueil';
  if (pathname.startsWith('/colis') || 
      pathname.startsWith('/send-package') ||
      pathname.startsWith('/create-shipment') ||
      pathname.startsWith('/my-shipments')) return 'colis';
  if (pathname.startsWith('/trajets') ||
      pathname.startsWith('/my-trip') ||
      pathname.startsWith('/publish-trip') ||
      pathname.startsWith('/publish') ||
      pathname.startsWith('/parcels')) return 'trajets';
  if (pathname.startsWith('/messages')) return 'messages';
  if (pathname.startsWith('/requests') ||
      pathname.startsWith('/notifications')) return 'accueil';
  if (pathname.startsWith('/profile') ||
      pathname.startsWith('/identity') ||
      pathname.startsWith('/verification') ||
      pathname.startsWith('/hero-status')) return 'profil';
  if (pathname.startsWith('/missions') ||
      pathname.startsWith('/mission/')) return 'accueil';
  return 'accueil';
}

// ── Desktop Sidebar ────────────────────────────────────
function DesktopSidebar({ unreadCount }) {
  const location = useLocation();
  const navigate = useNavigate();
  const { user } = useAuth();
  const activeTab = getActiveTab(location.pathname);

  return (
    <aside className="
      hidden md:flex flex-col
      fixed left-0 top-0 bottom-0
      w-[240px] z-40
      bg-white border-r border-[#e8e3dc]
    ">
      {/* Logo */}
      <div className="px-6 pt-7 pb-5">
        <span className="font-['Space_Grotesk'] text-[20px] font-bold
                         text-[#1f1f1f] tracking-[-0.5px]">
          flitship
        </span>
        <p className="text-[9px] text-[#a3a3a3] tracking-[1px]
                      uppercase mt-0.5 font-['Inter'] font-medium">
          Human Shipping Network
        </p>
      </div>

      {/* Navigation items */}
      <nav className="flex-1 px-3 py-4 space-y-0.5">
        {NAV_ITEMS.map((item) => {
          const isActive = activeTab === item.id;
          const Icon = item.Icon;
          const showBadge = item.hasBadge && unreadCount > 0;

          return (
            <button
              key={item.id}
              onClick={() => navigate(item.route)}
              className={`
                w-full flex items-center gap-3
                px-3 py-2.5 rounded-[10px]
                text-left transition-all duration-150
                ${isActive
                  ? 'bg-[#eff4ff] text-[#3c6df0]'
                  : 'text-[#737373] hover:bg-[#faf6f2] hover:text-[#1f1f1f]'
                }
              `}
            >
              <div className="relative flex-shrink-0">
                <Icon isActive={isActive} />
                {showBadge && (
                  <span className="
                    absolute -top-1 -right-1.5
                    min-w-[14px] h-[14px] px-0.5
                    flex items-center justify-center
                    bg-[#f38a3e] text-white
                    text-[8px] font-bold rounded-full
                  ">
                    {unreadCount > 9 ? '9+' : unreadCount}
                  </span>
                )}
              </div>
              <span className={`
                text-[14px] font-['Space_Grotesk']
                ${isActive
                  ? 'font-semibold text-[#3c6df0]'
                  : 'font-medium text-[#737373]'
                }
              `}>
                {item.label}
              </span>
              {isActive && (
                <div className="ml-auto w-1.5 h-1.5 rounded-full
                                bg-[#3c6df0]" />
              )}
            </button>
          );
        })}
      </nav>

      {/* Admin link for admin@flitship.com */}
      {user?.email === 'admin@flitship.com' && (
        <div className="px-3 pb-2">
          <button
            onClick={() => navigate('/admin')}
            className="w-full flex items-center gap-3 px-3 py-2.5 rounded-[10px] text-left transition-all text-[#737373] hover:bg-[#faf6f2] hover:text-[#3c6df0]"
          >
            <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
              <rect x="1" y="1" width="6" height="6" rx="1.5" stroke="currentColor" strokeWidth="1.3"/>
              <rect x="9" y="1" width="6" height="6" rx="1.5" stroke="currentColor" strokeWidth="1.3"/>
              <rect x="1" y="9" width="6" height="6" rx="1.5" stroke="currentColor" strokeWidth="1.3"/>
              <rect x="9" y="9" width="6" height="6" rx="1.5" stroke="currentColor" strokeWidth="1.3"/>
            </svg>
            <span className="font-['Space_Grotesk'] text-[14px] font-medium">Admin Dashboard</span>
          </button>
        </div>
      )}

      {/* Bottom — Trust badge */}
      <div className="px-5 py-5 border-t border-[#e8e3dc]">
        <div className="flex items-center gap-2 px-3 py-2.5
                        bg-[#faf6f2] rounded-[10px]">
          <svg width="14" height="14" viewBox="0 0 28 28" fill="none">
            <path d="M14 3L22 7V14C22 18.4 18.5 22.5 14 24C9.5 22.5 6 18.4 6 14V7L14 3Z"
              stroke="#4bb543" strokeWidth="2" fill="none"
              strokeLinecap="round" strokeLinejoin="round" />
            <path d="M10 14L13 17L18 11"
              stroke="#4bb543" strokeWidth="2" fill="none"
              strokeLinecap="round" strokeLinejoin="round" />
          </svg>
          <span className="text-[11px] font-['Inter'] font-medium
                           text-[#737373]">
            Trust Protocol™ actif
          </span>
        </div>
      </div>
    </aside>
  );
}

// ── AppLayout ──────────────────────────────────────────
export function AppLayout() {
  const { isAuthenticated, loading, getAccessToken, user } = useAuth();
  const location = useLocation();
  const { shouldShow: showOnboarding,
          markComplete: completeOnboarding } = useWelcomeOnboarding();
  const [unreadCount, setUnreadCount] = useState(0);

  const fetchUnread = useCallback(async () => {
    try {
      const token = await getAccessToken();
      if (!token) return;
      const res = await fetch(`${API}/api/notifications/unread-count`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (res.ok) {
        const d = await res.json();
        setUnreadCount(d.count || 0);
      }
    } catch { /* silent */ }
  }, [getAccessToken]);

  useEffect(() => {
    if (isAuthenticated) {
      fetchUnread();
      const interval = setInterval(fetchUnread, 30000);
      return () => clearInterval(interval);
    }
  }, [isAuthenticated, fetchUnread]);

  // Scroll reset on route change
  useEffect(() => {
    window.scrollTo({ top: 0, left: 0, behavior: 'instant' });
  }, [location.pathname]);

  if (loading) {
    return (
      <div className="min-h-screen bg-[#faf6f2] flex items-center
                      justify-center">
        <div className="w-8 h-8 border-2 border-[#3c6df0]
                        border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  if (!isAuthenticated) {
    return <Navigate to="/login" state={{ from: location }} replace />;
  }

  return (
    <div className="min-h-screen bg-[#faf6f2]">

      {/* Welcome onboarding — first time only */}
      {showOnboarding && (
        <WelcomeOnboarding onComplete={completeOnboarding} />
      )}

      {/* Desktop sidebar */}
      <DesktopSidebar unreadCount={unreadCount} />

      {/* Main content — offset by sidebar on desktop */}
      <main className="
        pb-20 md:pb-0
        md:ml-[240px]
        min-h-screen
        bg-[#faf6f2]
      ">
        <div className="flex-1 overflow-y-auto pb-[calc(58px+env(safe-area-inset-bottom,0px))] md:pb-0">
          <Outlet />
        </div>
      </main>

      {/* Bottom navigation — mobile only */}
      <BottomNavigation />
    </div>
  );
}

/**
 * PublicLayout - Layout for public pages (no navigation)
 */
export function PublicLayout() {
  const { isAuthenticated } = useAuth();
  const location = useLocation();

  const allowedForAuthenticated = ['/diagnostic', '/item-check', '/identity-verification', '/email-confirmation', '/email-confirmed', '/update-password'];
  const isAllowedPath = allowedForAuthenticated.some(
    path => location.pathname.startsWith(path)
  );

  const isLegalPath = location.pathname.startsWith('/legal');

  if (isAuthenticated && !isAllowedPath && !isLegalPath) {
    return <Navigate to="/app" replace />;
  }
  return <Outlet />;
}

export default AppLayout;
